import Image from "@/components/ui/Image/Image";
import { Reward } from "@/store/rewards/reward.model";
import { useListRewrads } from "@/store/rewards/rewards.service";
import { RewardsStore } from "@/store/rewards/rewards.store";
import { SettingsStore } from "@/store/settings/settings.store";

import { useBrandConfig } from "@/utils/useBrandConfig";
import { useResponsive } from "@/utils/useResponsive";
import { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { enGB, fr } from "date-fns/locale";
import { Locales } from "@/constants/locales.constants";
import { ServiceDrawer } from "@/components/ServiceDrawer/ServiceDrawer";

import DateIcon from "@/assets/svgs/date.svg?react";
import { Button } from "@/components/ui/Button/Button";

export const RewardsScreen = () => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const { data: rewards } = useListRewrads();
  const setRewards = RewardsStore((state) => state.setRewards)
  const { futureRewards, pastRewards } = RewardsStore();

  const [serviceDrawerOpen, setServiceDrawerOpen] = useState(false);
  const [serviceDrawerContent, setServiceDrawerContent] = useState<ReactNode>(null);

  useEffect(() => {
    if (rewards) {
      setRewards(rewards);
    }
  }, [rewards, setRewards]);

  const Header = () => (
    <div className={`flex flex-col gap-y-2 items-start ${isMobile ? 'px-1' : ''}`}>
      <p className={'text-headingXSmall'}>{t("header.REWARDS")}</p>
      <p className="opacity-50">{t('rewards.subtitle')}</p>
    </div>
  );

  const Empty = () => (
    <div className={`flex flex-col flex-1 items-center justify-center text-center gap-y-3`}>
      <p className="text-headingMedium">{t('rewards.empty')}</p>
    </div>
  );

  const handleClick = (reward: Reward) => {
    setServiceDrawerContent(<RewardDetails reward={reward} />);
    setServiceDrawerOpen(true)
  }

  return (
    <div className={`flex flex-col flex-1 ${isMobile ? 'pt-8 px-4' : 'px-16 py-6 xl:px-16 md:px-8 '}`}>
      <Header />
      {futureRewards.length || pastRewards.length ? <>
        <div className={"grid gap-8 items-start lg:py-8 py-6 xl:grid-cols-3 lg:grid-cols-2 md:grid-cols-2 sm:grid-cols-1 place-items-center"}>
          {futureRewards.map((reward, index) => (
            <RewardCard
              key={index}
              reward={reward}
              onClick={() => handleClick(reward)}
            />
          ))}
        </div>
        <div className="w-full border-t border-gray-200">
          <p className="text-labelSmallBold opacity-50 py-4">{t("rewards.past_rewards")} ({pastRewards.length})</p>
          <div className={"grid gap-8 items-start xl:grid-cols-3 lg:grid-cols-2 md:grid-cols-2 sm:grid-cols-1 place-items-center"}>
            {pastRewards.map((reward, index) => (
              <RewardCard
                reward={reward}
                key={index}
                onClick={() => handleClick(reward)}
              />
            ))}
          </div>
        </div>
      </> : <Empty />
      }
      <ServiceDrawer
        open={serviceDrawerOpen}
        setOpen={setServiceDrawerOpen}
        content={serviceDrawerContent}
      />
    </div>
  )
}

type RewardCardProps = {
  reward: Reward;
  onClick: () => void;
}
export const RewardCard = ({ reward, onClick }: RewardCardProps) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const { languages } = useBrandConfig();

  const lang = SettingsStore.getState().getLanguage();
  const defaultLangage = languages[0]

  const title = reward.translations[lang]?.title || reward.translations[defaultLangage]?.title;
  const { startDate, endDate } = reward;

  const formatDate = (date: Date): string => {
    return format(date, 'dd/MM/yyyy', { locale: lang === Locales.FR ? fr : enGB });
  }

  return (
    <div
      className={`w-full h-full bg-[#F6F6F6] hover:bg-[#FAFAFA] rounded-lg p-2.5 flex flex-col gap-y-[18px] cursor-pointer ${isMobile ? '' : 'max-w-[420px] min-w-[400px]'}`}
      onClick={onClick}
    >
      <Image
        src={reward.image}
        className="w-full rounded-lg h-[210px] object-cover"
      />
      <div className="flex flex-col justify-between px-4 gap-y-2 h-full">
        <div className="text-headingMedium">{title}</div>
        <div className="text-textMedium opacity-50">{t("rewards.from")} {formatDate(startDate)} {t("rewards.to")} {formatDate(endDate)}</div>
      </div>
    </div>
  )
}

export const RewardDetails = ({ reward }: { reward: Reward }) => {
  const { languages } = useBrandConfig();
  const { t } = useTranslation();

  const lang = SettingsStore.getState().getLanguage();
  const defaultLangage = languages[0]

  const title = reward.translations[lang]?.title || reward.translations[defaultLangage]?.title;
  const description = reward.translations[lang]?.description || reward.translations[defaultLangage]?.description;
  const ctaTitle = reward.translations[lang]?.ctaTitle || reward.translations[defaultLangage]?.ctaTitle;
  const ctaUrl = reward.translations[lang]?.ctaUrl || reward.translations[defaultLangage]?.ctaUrl;
  const { startDate, endDate } = reward;

  const formatDate = (date: Date): string => {
    return format(date, 'dd/MM/yyyy', { locale: lang === Locales.FR ? fr : enGB });
  }

  const now = new Date();
  const end = new Date(endDate);
  const isExpired = end < now;

  return (
    <div className="pl-4 pr-6 py-5 overflow-y-auto min-h-dvh flex flex-col justify-between items-center">
      <div className="flex flex-col w-full">
        <p className="text-titleLarge pr-8 pb-6">{title}</p>
        <Image
          src={reward.image}
          className="w-full rounded-lg"
        />
        <p className="pb-[18px] pt-6 text-sm">{description}</p>
        <div className="flex flex-row justify-between border-b border-gray-200 py-6">
          <div className="flex flex-row items-center gap-x-3">
            <DateIcon />
            <div className="text-textMedium opacity-50">{t("rewards.date")}</div>
          </div>
          <div className="text-textMedium opacity-50">{t("rewards.from")} {formatDate(startDate)} {t("rewards.to")} {formatDate(endDate)}</div>
        </div>
      </div>
      {!isExpired && ctaTitle && ctaUrl &&
        <div className="items-end pt-5">
          <Button
            primary
            label={ctaTitle}
            onClick={() => { window.open(ctaUrl) }}
          />
        </div>
      }
    </div>
  )
}